import React, { useState } from 'react';
import { useStore, productSubcat } from '../context/StoreContext';
import { Icons } from './Icons';
import SmartImage from './SmartImage';
import Link from './Link';
import { contrastText } from '../utils/colorNames';
import { colorOptionsFor } from '../utils/productColors';
import useMediaQuery from '../utils/useMediaQuery';
import './ProductCard.css';

/* At most this many colour dots before we show a "+n" pill instead. */
const MAX_SWATCHES = 4;

/**
 * A single product tile.
 *
 * Every card is the same height regardless of how long the product
 * name is: the card is a flex column, the footer is pushed down with
 * `margin-top:auto`, and the name is clamped to two lines. The image
 * sits in a fixed 1:1 ratio box so a portrait photo and a square one
 * line up perfectly next to each other.
 *
 * Colour handling: whichever colour is selected here is the colour that
 * ends up in the cart and in the WhatsApp order message. On a phone a
 * card is about 105px wide, so a row of tappable dots would be far below
 * a usable touch target — there, a multi-colour product sends the
 * shopper to the product page to choose properly instead.
 */
export default function ProductCard({ product, priority = false }) {
  const { addToCart, viewProduct, Rs } = useStore();
  const { id, name, cat, price, oldPrice, badge, img, images, stock } = product;

  const compact = useMediaQuery('(max-width: 767px)');
  const options = colorOptionsFor(product);
  const multiColor = options.length > 1;

  /* Default to the first colour — the one the admin set (or the dominant
     detected colour when none was set) — so something truthful is always
     attached to the order. */
  const [chosen, setChosen] = useState(null);
  const active = chosen || options[0] || null;

  const sub = productSubcat(product);
  const open = () => viewProduct(id);
  const href = '/product/' + encodeURIComponent(id);
  const cover = (Array.isArray(images) && images[0]) || img || '';

  const discount = oldPrice && oldPrice > price
    ? Math.round(((oldPrice - price) / oldPrice) * 100)
    : 0;

  const outOfStock = typeof stock === 'number' && stock <= 0;

  /* On a narrow screen, a product with a real colour choice goes to the
     detail page rather than guessing on the shopper's behalf. */
  const needsColorPage = multiColor && compact;

  const shown = options.slice(0, MAX_SWATCHES);
  const overflow = options.length - shown.length;

  const handleCta = (e) => {
    e.stopPropagation();
    if (needsColorPage) { open(); return; }
    addToCart(id, active ? { color: { name: active.name, hex: active.hex } } : {});
  };

  const ctaLabel = outOfStock
    ? 'Sold out'
    : needsColorPage
      ? 'Choose colour'
      : 'Add to cart';

  return (
    <article className={`product-card${outOfStock ? ' is-out' : ''}`}>
      <div className="product-media">
        <Link href={href} onClick={open} className="product-media__link" aria-label={name} tabIndex={-1}>
          <SmartImage
            src={(active?.hasPhotos && product.colorImages[active.name][0]) || cover}
            alt={name}
            priority={priority}
            className="product-media__img"
            fallback={<span className="product-placeholder">{Icons.diamond(48)}</span>}
          />
        </Link>

        <div className="product-flags">
          {badge && <span className="product-badge">{badge}</span>}
          {discount > 0 && <span className="product-badge product-badge--sale">-{discount}%</span>}
        </div>

        {outOfStock && <div className="product-oos">Sold out</div>}

        <span className="product-quickview" aria-hidden="true">View details</span>
      </div>

      <div className="product-body">
        <p className="product-eyebrow">
          {cat}
          {sub && <span className="product-eyebrow__sub"> · {sub}</span>}
        </p>

        <h3 className="product-name">
          <Link href={href} onClick={open}>{name}</Link>
        </h3>

        {options.length > 0 && (
          <div className="product-colors">
            {multiColor && !compact ? (
              <fieldset className="product-swatches product-swatches--pick">
                <legend className="sr-only">Choose a colour for {name}</legend>
                {shown.map((c) => {
                  const isActive = active && active.name === c.name;
                  return (
                    <button
                      key={c.name}
                      type="button"
                      className={`product-swatch-btn${isActive ? ' is-active' : ''}`}
                      style={{ background: c.hex, color: contrastText(c.hex) }}
                      title={`${c.name}${c.hex.startsWith('#') ? ' — ' + c.hex : ''}`}
                      aria-label={c.name}
                      aria-pressed={isActive}
                      onClick={(e) => { e.stopPropagation(); setChosen(c); }}
                    />
                  );
                })}
                {overflow > 0 && (
                  <Link href={href} onClick={open} className="product-swatch-more" title="See all colours">
                    +{overflow}
                  </Link>
                )}
              </fieldset>
            ) : (
              <ul className="product-swatches" aria-label="Available colours">
                {shown.map((c) => (
                  <li key={c.name}>
                    <span
                      className="product-swatch"
                      style={{ background: c.hex }}
                      title={c.name}
                    />
                  </li>
                ))}
                {overflow > 0 && <li className="product-swatch-more">+{overflow}</li>}
              </ul>
            )}

            {/* Say plainly which colour is going on the order */}
            {active && !compact && (
              <span className="product-color-name">{active.name}</span>
            )}
          </div>
        )}

        <div className="product-foot">
          <div className="product-price">
            {oldPrice > price && <span className="product-price__old">{Rs(oldPrice)}</span>}
            <span className="product-price__now">{Rs(price)}</span>
          </div>

          <button
            className="product-cta"
            onClick={handleCta}
            disabled={outOfStock}
            aria-label={
              needsColorPage
                ? `Choose a colour for ${name}`
                : active
                  ? `Add ${name} in ${active.name} to cart`
                  : `Add ${name} to cart`
            }
          >
            {needsColorPage ? Icons.diamond(15) : Icons.cart(15)}
            <span>{ctaLabel}</span>
          </button>
        </div>
      </div>
    </article>
  );
}
